import classNames from 'classnames';
import { CSSProperties, forwardRef, useMemo } from 'react';
import type { LinkInfo, Size } from '../../types';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import LinkInfoRenderer from '../LinkInfoRenderer';
import LoadingSpinner from '../LoadingSpinner';
import styles from './styles/IconButton.module.scss';

type IconButtonProps = {
  className?: string;
  disabled?: boolean;
  icon: IconDefinition<'orientation' | 'color'>;
  linkInfo?: LinkInfo;
  loading?: boolean;
  onBlur?: () => void;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  onFocus?: () => void;
  size?: Size<'sm' | 'md' | 'lg'>;
  stopPropagation?: boolean;
  style?: CSSProperties;
  tabIndex?: number;
  testId?: string;
  theme?: 'light' | 'dark';
  title: string;
  ariaLabel?: string;
  ariaControls?: string;
  ariaExpanded?: boolean;
  ariaDescribedBy?: string;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  variant?: 'default' | 'elevated' | 'flat' | 'primary' | 'transparent' | 'accent';
  dataTooltipId?: string;
};

const IconButton = forwardRef(
  (
    {
      className,
      disabled,
      icon,
      linkInfo,
      loading,
      onBlur,
      onClick,
      onKeyDown,
      onFocus,
      size = 'md',
      stopPropagation = false,
      style,
      tabIndex,
      testId,
      theme, // Do not default this since it overrides a wrapping component's theme
      title,
      ariaLabel,
      ariaControls,
      ariaExpanded,
      ariaDescribedBy,
      type = 'button',
      variant = 'default',
      dataTooltipId
    }: IconButtonProps,
    ref: React.Ref<HTMLElement>
  ) => {
    const onButtonClick = useMemo(
      () => (e: React.MouseEvent<HTMLElement>) => {
        if (stopPropagation) {
          e.stopPropagation();
        }
        if (onClick) {
          onClick(e);
        }
      },
      [onClick, stopPropagation]
    );
    const handleKeyDown = useMemo(
      () => (e: React.KeyboardEvent<HTMLElement>) => {
        if (stopPropagation) {
          e.stopPropagation();
        }
        if (onKeyDown) {
          onKeyDown(e);
        }
      },
      [onKeyDown, stopPropagation]
    );
    const iconSize = size === 'sm' ? 'sm' : 'md';
    const content = useMemo(() => {
      if (loading && !disabled) {
        return (
          <>
            <LoadingSpinner size={iconSize} testId={`${testId}-loading-spinner`} />
          </>
        );
      }

      return (
        <>
          <IconRenderer icon={icon} defaults={{ color: 'currentColor', size: iconSize }} />
        </>
      );
    }, [disabled, icon, iconSize, loading, testId]);

    const classes = classNames(
      className,
      styles.button,
      styles[size],
      styles[variant],
      loading && styles.loading,
      disabled && styles.disabled // An anchor tag cannot be disabled, so we apply a disabled class instead
    );

    const buttonElement = useMemo(() => {
      if (linkInfo) {
        if (disabled) {
          return (
            <span className={classes} title={title} aria-label={ariaLabel || title} data-testid={testId} ref={ref}>
              {content}
            </span>
          );
        }

        return (
          <LinkInfoRenderer
            className={classes}
            dataTooltipId={dataTooltipId}
            linkInfo={linkInfo}
            onClick={loading ? undefined : onButtonClick}
            onKeyDown={loading ? undefined : handleKeyDown}
            title={title}
            ariaLabel={ariaLabel || title}
            testId={testId}
            tabIndex={tabIndex}
            ref={ref as React.Ref<HTMLAnchorElement>}
          >
            {content}
          </LinkInfoRenderer>
        );
      }

      return (
        <button
          {...(theme ? { 'data-at-theme-colors': theme } : {})}
          className={classes}
          onClick={loading ? undefined : onButtonClick}
          onKeyDown={loading ? undefined : handleKeyDown}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={disabled}
          title={title}
          aria-label={ariaLabel || title}
          data-testid={testId}
          aria-controls={ariaControls}
          aria-expanded={ariaExpanded}
          aria-describedby={ariaDescribedBy}
          type={type}
          tabIndex={tabIndex}
          data-tooltip-id={dataTooltipId}
          ref={ref as React.Ref<HTMLButtonElement>}
          style={style}
        >
          {content}
        </button>
      );
    }, [
      linkInfo,
      theme,
      classes,
      loading,
      onButtonClick,
      handleKeyDown,
      onBlur,
      onFocus,
      disabled,
      title,
      ariaLabel,
      testId,
      ariaControls,
      ariaExpanded,
      ariaDescribedBy,
      type,
      tabIndex,
      dataTooltipId,
      ref,
      content,
      style
    ]);

    return buttonElement;
  }
);

export default IconButton;
